import { ListResponse } from "./types";
import rest, { api, specUrl } from "./api";
import type { EncryptedMedia } from "./encrypted-media";

export interface GetChatHistory {
  messageId: bigint;
  roomId: string;
  limit?: number;
}

export interface IData {
  type: number;
  data: any;
}

export enum MessageType {
  Text = 0,
  Image = 1,
  Video = 2,
  ClearedHistory = 3,
  UpdatedRoom = 4,
  AddedNewMember = 5,
  RemovedMember = 6,
  MemberLeft = 7,
  File = 14,
  // Encryption boundaries: quiet system messages, body `{"userId"}` (who changed it).
  EncryptionOn = 15,
  EncryptionOff = 16,
  // Server->client action frames (message.fbs), `messageId` = the target.
  // Never a message: see `isMessageAction`.
  Unsent = 11,
  Reaction = 12,
  Hidden = 13,
}

export enum MessageStatus {
  SENT = 0,
  DELIVERED = 1,
  READ = 2,
  SENDING = 3,
  FAILED = 4,
}

export interface IChat {
  roomId: string;
  bucket: bigint;
  status?: MessageStatus | number;
  messageId: bigint;
  clientMessageId?: bigint;
  userId: string;
  message: string;
  parentMessageId?: bigint;
  parentMessageText?: string;
  /** @deprecated use parentMessageText for text or fetch full message */
  parentMessage?: IChat;
  type?: MessageType;
  url?: string;
  fileUrl?: string;
  /** Server-stamped for an attachment (history, sync and live frames); absent otherwise. */
  attachmentName?: string;
  attachmentSize?: number;
  attachmentContentType?: string;
  /**
   * An Encrypted message as `/chat/history` returns it (base64; `message` is then empty) and the
   * epoch it belongs to. Read by the e2ee layer to find the plaintext; never rendered.
   */
  ciphertext?: string;
  epoch?: number;
  /**
   * An Encrypted message this Device could not open. `message` is empty: the app draws the
   * "can't read on this device" copy, which is never stored, so it cannot replace real text.
   */
  unreadable?: boolean;
  /** Set once the message came as an Encrypted one (readable or not). Encrypted messages are never reported. */
  encrypted?: boolean;
  /** An unreadable one because this app is older than the message (a newer envelope version): the copy says to update. */
  unsupported?: boolean;
  /**
   * An Encrypted media message this Device opened: what its envelope says (key, name, size, ...).
   * `message` is then the storage key. Kept in the local store; never sent anywhere.
   */
  media?: EncryptedMedia;
  /** Set on a pending message the server refused: a `RefusalCode`. */
  refusalCode?: number;
  timestamp?: number;
  /** Unix ms the sender unsent it; the message is then a tombstone (no body, no url). */
  unsentAt?: number;
  /** emoji -> count. */
  reactions?: Record<string, number>;
  /** The reader's own emoji. */
  myReaction?: string;
  /**
   * identity -> emoji, learned from live REACTION frames only (history carries
   * counts alone). It is what lets a later frame take the old emoji back off.
   */
  reactors?: Record<string, string>;
}

/** A message and its room: the body of unsend, hide and reactions. */
export interface MessageRef {
  roomId: string;
  messageId: bigint;
}

export interface MessageEvent {
  eventId: bigint;
  type: number;
  messageId: bigint;
  userId: string;
  body: string;
}

export interface ChatMessage {
  messageId: number;
  roomId: string;
  userId: string;
  message: string;
  type: number;
  status: number;
  bucket: number;
  url?: string;
  parentMessageId?: number;
  attachmentName?: string;
  attachmentSize?: number;
  attachmentContentType?: string;
  seenAt?: number;
  unsentAt?: number;
  reactions?: Record<string, number>;
  myReaction?: string;
}

export interface SyncResponse {
  nextSyncCursor: number;
  rooms: Array<{ roomId: string; messages: ChatMessage[]; events?: MessageEvent[] }>;
}

const chatService = {
  // messageId/bucket are int64 snowflakes: the spec types them `number`, which
  // cannot hold them. Keep the bigint shapes and check only the URL.
  getHistory: (body: GetChatHistory) =>
    rest.post<ListResponse<IChat>>(specUrl("chat/history"), {
      body,
      hasAuth: true,
    }),
  syncMessages: (opts?: { lastMessageID?: number; syncCursor?: number }) =>
    rest.get<SyncResponse>(specUrl("sync"), {
      hasAuth: true,
      params: opts
        ? Object.fromEntries(
            Object.entries(opts)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)])
          )
        : undefined,
    }),
  // Ids are snowflakes, so these keep the bigint `MessageRef` and check only the URL.
  unsend: (body: MessageRef) =>
    rest.post<unknown>(specUrl("chat/message/unsend"), { body, hasAuth: true }),
  hide: (body: MessageRef) =>
    rest.post<unknown>(specUrl("chat/message/hide"), { body, hasAuth: true }),
  /** The same emoji again, or `null`, removes the caller's reaction. */
  react: (body: MessageRef & { emoji: string | null }) =>
    rest.post<unknown>(specUrl("chat/message/react"), { body, hasAuth: true }),
  reactions: (body: MessageRef) =>
    rest.post<{ items: Array<{ identity: string; emoji: string }> }>(
      specUrl("chat/message/reactions"),
      { body, hasAuth: true }
    ),
};

export default chatService;

/**
 * How long `/features` may take. The web socket waits for it (it must know whether Encrypted
 * messages are on before it reads any), so a hung request would keep chat shut: past this the
 * answer is "off", as for a failed request.
 */
export const FEATURES_TIMEOUT_MS = 3000;

/** Off, the message-action routes refuse with 3027: show no message-action UI. */
export const getFeatures = () => api.get("features", { timeout: FEATURES_TIMEOUT_MS });

/**
 * Frames that act on an existing message (`messageId` is the target). They are
 * not chat messages: never a bubble, a preview, an unread, or a presign.
 */
export const isMessageAction = (type: number | undefined): boolean =>
  type === MessageType.Unsent ||
  type === MessageType.Reaction ||
  type === MessageType.Hidden;

export const SYSTEM_MESSAGE_TYPES = [
  MessageType.ClearedHistory,
  MessageType.UpdatedRoom,
  MessageType.AddedNewMember,
  MessageType.RemovedMember,
  MessageType.MemberLeft,
  MessageType.EncryptionOn,
  MessageType.EncryptionOff,
];

/**
 * The quiet system frame that says a Knot's chat is sealed or unsealed: an
 * `UPDATED_ROOM` whose body is exactly `{id, sealedUntil}`. It moves the banner and the
 * composer, never a bubble, a preview or an unread. Returns `undefined` for any other
 * message, and `{ sealedUntil: null }` for the unseal.
 */
export function sealFrame(msg: { type?: number; message?: string }): { sealedUntil: string | null } | undefined {
  if (msg.type !== MessageType.UpdatedRoom) return undefined;
  try {
    const p = JSON.parse(msg.message ?? "");
    if (!p || typeof p !== "object" || Array.isArray(p)) return undefined;
    const keys = Object.keys(p);
    if (!("sealedUntil" in p) || keys.some((k) => k !== "id" && k !== "sealedUntil")) return undefined;
    return { sealedUntil: typeof p.sealedUntil === "string" ? p.sealedUntil : null };
  } catch {
    return undefined;
  }
}

/**
 * The handover frame: an `UPDATED_ROOM` whose body is `{id, admin, kind: "admin_changed"}`,
 * `admin` being the new admin's user id. Returns that id, or `undefined` for any other message.
 */
export function handoverFrame(msg: { type?: number; message?: string }): { admin: string } | undefined {
  if (msg.type !== MessageType.UpdatedRoom) return undefined;
  try {
    const p = JSON.parse(msg.message ?? "");
    if (!p || typeof p !== "object" || p.kind !== "admin_changed" || typeof p.admin !== "string") return undefined;
    return { admin: p.admin };
  } catch {
    return undefined;
  }
}

export const isChatNotifType = (type: MessageType | undefined): boolean =>
  type !== undefined ? SYSTEM_MESSAGE_TYPES.includes(type) : false;

/** Snowflake epoch, shared by the encoder and decoder below. */
const SNOWFLAKE_EPOCH = 1288834974657;
const SNOWFLAKE_SHIFT = 4194304;

/**
 * A cursor meaning "the newest messages".
 *
 * `/chat/history` pages *backwards* from a message id, so the first page of a
 * room is requested with a snowflake at the current instant. Passing 0 asks for
 * everything older than the epoch and comes back empty.
 */
export const newestMessageCursor = (): bigint =>
  BigInt((Date.now() - SNOWFLAKE_EPOCH) * SNOWFLAKE_SHIFT);

export const getTimeFromMessageId = (messageId: bigint): Date => {
  const ts = Math.floor(Number(messageId) / SNOWFLAKE_SHIFT);
  return new Date(Math.floor(SNOWFLAKE_EPOCH + ts));
};

export const lastRealMessageId = (messages: IChat[]): bigint | undefined => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (!isChatNotifType(messages[i].type)) return messages[i].messageId;
  }
  return undefined;
};
